// Buyer-side change detection. While a buy modal is open it POLLS the tiny
// live-state endpoint so a seller edit (new price / allow_bidding) or a sale
// completed by someone else is noticed and surfaced as a "listing updated"
// banner instead of letting the buyer pay a stale amount.
//
//   GET /api/v1/marketplace/listings/share/{id}
//   GET /api/v1/marketplace/listings/sealed-dossier/{id}
//   → { listing_id, status, price_cents, allow_bidding }
//
// queryKey: ["listing-live-state", kind, listingId]. Polls every 6s while
// `enabled`; short staleTime so a manual refetch is always live.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ListingKind = "share" | "sealed_dossier";

export interface ListingLiveState {
  listing_id: string;
  status: string;
  price_cents: number;
  allow_bidding: boolean;
}

export interface ListingLiveStateError {
  code: string;
  message: string;
  status: number;
}

function endpointFor(kind: ListingKind, id: string): string {
  return kind === "share"
    ? `/api/v1/marketplace/listings/share/${id}`
    : `/api/v1/marketplace/listings/sealed-dossier/${id}`;
}

async function fetchLiveState(
  kind: ListingKind,
  listingId: string,
): Promise<ListingLiveState> {
  const response = await fetch(endpointFor(kind, listingId), {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ListingLiveStateError = {
      code: "LISTING_LIVE_STATE_FAILED",
      message: `Live-state fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: ListingLiveState };
  return body.data;
}

export function useListingLiveState(
  kind: ListingKind,
  listingId: string | null,
  enabled: boolean,
): UseQueryResult<ListingLiveState, ListingLiveStateError> {
  return useQuery<ListingLiveState, ListingLiveStateError>({
    queryKey: ["listing-live-state", kind, listingId],
    queryFn: () => fetchLiveState(kind, listingId as string),
    enabled: enabled && listingId !== null,
    // Poll while the modal is open; a stale display is exactly what we're
    // guarding against, so keep it fresh.
    refetchInterval: 6000,
    staleTime: 0,
    retry: false,
  });
}
